import { payAgainApi } from '@/api/order'
import {
  wechatPay,
  wxMpPay
} from '@/utils/payment/wechatPay'
import store from '@/store'
import { judgeUserAgent } from '@/utils/judgeUserAgent'
import { getuser } from '@/api/user'

/**
 * 获取用户 openId
 *
 * JSAPI 支付必须携带 openid, 不能返回空值兜底, 否则会被服务端拒绝
 * (报 "JSAPI支付必须传openid"). Vuex 里的 userInfo 可能出现两种空档:
 * 1. 路由守卫的 fetchData 未 await, 用户抢先点支付时 userInfo 还是初始值 ''
 * 2. userInfo 一旦缓存就不再刷新, 绑定微信之前进入的会话拿不到 openId
 * 所以缓存缺失时实时拉取, 并回填缓存供后续使用.
 * @returns {Promise<string>} 微信 openId
 * @throws {Error} 用户信息获取失败或未绑定微信
 */
const getOpenId = async () => {
  const cachedOpenId = store.getters.userInfo?.openId
  if (cachedOpenId) return cachedOpenId

  const res = await getuser()
  const userData = res?.data?.data
  if (!userData || !userData.status) {
    throw new Error('获取用户信息失败, 请重试')
  }
  if (!userData.isBindWechat || !userData.openId) {
    throw new Error('请先绑定微信后再使用微信支付')
  }

  store.commit('user/SET_USERINFO', userData)
  return userData.openId
}

/**
 * 获取微信支付类型
 * @returns {number} 1: JSSDK | 2: WeixinJSBridge | 3: 小程序 | 4: H5
 */
const getWxPayType = () => {
  if (judgeUserAgent('WXMP')) return 3 // 微信小程序
  if (judgeUserAgent('WXWEB')) return 2 // 微信公众号
  if (judgeUserAgent('WXH5')) return 4 // 非微信浏览器
  return 1
}

/**
 * 处理项目卡支付
 */
const handleProjectCardPay = data => {
  const payment = data.payment

  if (
    payment.pay_status === false &&
    payment.pay_data.jump_to_payment === true
  ) {
    window.location.href =
      payment.pay_data.jump_to_payment_url
    return null
  }

  return data
}

/**
 * 处理微信支付
 */
const handleWechatPay = async (payData, orderSn, payId) => {
  const weiChatPay = await wechatPay({
    payData,
    order_sn: orderSn,
    pay_id: payId
  })

  return {
    status: weiChatPay.status,
    msg: weiChatPay.status ? '支付成功' : weiChatPay.msg
  }
}

/**
 * 单笔支付处理
 */
const paymentAgain = async data => {
  const wxPayType = getWxPayType()
  // 小程序环境微信支付
  if (wxPayType === 3 && data.pay_type === 3) {
    wxMpPay({
      pay_id: data.pay_id,
      order_sn: data.order_sn
    })
    return Promise.reject('支付中')
  }

  // JSAPI (1: JSSDK | 2: 公众号) 必须携带 openid, 缺失时直接中断;
  // H5 支付 (4) 走 mweb_url, 不强制要求 openid
  const isJsapiPay = wxPayType === 1 || wxPayType === 2

  let openid
  if (data.pay_type === 3) {
    openid = isJsapiPay
      ? await getOpenId()
      : store.getters.userInfo?.openId || ''
  }

  const params = {
    pay_id: data.pay_id,
    openid,
    wx_pay_type: data.pay_type === 3 ? wxPayType : undefined
  }

  const result = await payAgainApi(params)

  if (!result.data.data.status) {
    return result.data.data
  }

  // 项目卡支付
  if (data.pay_type === 23) {
    return handleProjectCardPay(result.data.data)
  }

  // 微信支付
  if (data.pay_type === 3) {
    const payData = result.data.data.payment.pay_data
    return handleWechatPay(
      payData,
      data.order_sn,
      data.pay_id
    )
  }

  return result.data.data
}

/**
 * 批量支付处理（支持混合支付）
 * @param {Object} context - Vuex context
 * @param {Array} data - 支付信息数组
 * @param {string} data[].pay_id - 支付编号
 * @param {number} data[].payType - 支付类型（1:积分 | 2:余额 | 3:微信 | 20:混合 | 21:福利卡 | 23:项目卡）
 * @param {string} data[].order_sn - 订单编号
 */
const payAgain = async ({ dispatch }, data) => {
  const results = []
  // order_sn 挂在数组元素上, 不是数组本身
  const payList = (Array.isArray(data)
    ? data
    : [data]
  ).filter(Boolean)


  // 微信支付排在最后, 用副本排序避免改动调用方传入的数组
  const sortedData = [...payList].sort(
    (a, b) => (a.payType === 3) - (b.payType === 3)
  )

  try {
    for (const payInfo of sortedData) {
      const result = await paymentAgain({
        pay_id: payInfo.pay_id,
        pay_type: payInfo.payType,
        order_sn: payInfo.order_sn
      })

      results.push(result)

      // 项目卡已跳转外部支付页, result 为 null, 中断后续流程
      if (!result) {
        return Promise.resolve({
          status: false,
          msg: '支付跳转中'
        })
      }

      if (!result.status) {
        return Promise.resolve(result)
      }
    }

    return Promise.resolve({
      status: true,
      msg: '支付成功'
    })
  } catch (error) {
    // error 可能是 Error 实例(openId 校验失败)或字符串(reject('支付中'))
    return Promise.resolve({
      status: false,
      msg: error?.message ?? error ?? '支付失败'
    })
  }
}

export default payAgain
